import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Shape of the shared cross-subdomain `tr_consent` cookie, written by both the
 * marketing site (www.tablesready.com) and this app (host.tablesready.com).
 */
export interface ConsentState {
  v: number;
  analytics: boolean;
  ads: boolean;
  embeds: boolean;
  gpc: boolean;
  ts: number;
}

/** Categories the app's banner lets the user choose. The app only surfaces
 *  analytics; `ads`/`embeds` are carried over from any prior marketing choice. */
export interface ConsentChoice {
  analytics: boolean;
  ads: boolean;
  embeds: boolean;
}

/**
 * Angular port of the marketing site's consent manager
 * (tablesready_marketing/src/assets/js/consent.js). It reads/writes the shared
 * `tr_consent` cookie, mirrors Google Consent Mode v2 updates, and pushes the
 * `tr_consent_update` dataLayer signal so the GTM container can gate tags.
 *
 * The app treats anything it loads itself (Datadog RUM, Angulartics, Recurly)
 * as strictly necessary, so those are NOT gated here — this service only carries
 * consent state across to GTM and persists the user's choice. Authenticated app
 * users are auto opted-in to analytics via grantAnalyticsConsent() per the Terms
 * of Service "Consent to Analytics" clause.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private static readonly COOKIE = 'tr_consent';
  private static readonly VERSION = 1;
  private static readonly TTL_SECONDS = 60 * 60 * 24 * 180; // 180 days
  private static readonly MS_PER_SEC = 1000;

  /** Global Privacy Control is a binding opt-out of sale/share → forces ads off. */
  private readonly gpc = (navigator as any).globalPrivacyControl === true;

  /**
   * The native (iOS) app never grants advertising consent. Loading the GTM ad
   * tags (Google Ads, Bing) inside the Capacitor web view would be cross-app
   * tracking that requires Apple's App Tracking Transparency prompt, which the
   * app doesn't implement — and there's no ad click-through to the app to track
   * anyway. First-party analytics stays on (per the ToS); ads are forced off in
   * every consent computation via adsGranted(), and the web banner is suppressed.
   */
  private readonly isNative = Capacitor.isNativePlatform();

  /** Exposed for the banner so it can disable the ads toggle and show the GPC note. */
  get gpcEnabled(): boolean {
    return this.gpc;
  }

  /**
   * Effective advertising grant: requires a positive choice AND no GPC opt-out
   * AND a non-native platform. The native app never grants ads (see isNative).
   */
  private adsGranted(ads: boolean): boolean {
    return ads && !this.gpc && !this.isNative;
  }

  private readonly showBannerSubject = new BehaviorSubject<boolean>(false);
  /** True when the banner should be shown (no cookie yet for this visitor). */
  readonly showBanner$: Observable<boolean> = this.showBannerSubject.asObservable();

  /**
   * Serialized consent of the last emitted signal, used to dedupe. Several places
   * apply consent on a single load (e.g. AppComponent + AuthenticatedLayout on an
   * authenticated reload), so we only push tr_consent_update / gtag update when the
   * effective state actually changes.
   */
  private lastEmittedKey: string | null = null;

  /**
   * On startup: carry over any existing (marketing or prior app) consent to GTM.
   * If there is no cookie yet, publish the denied baseline and request the banner.
   */
  init(): void {
    const existing = this.read();
    if (existing) {
      this.apply(existing);
      return;
    }
    this.apply({ v: ConsentService.VERSION, analytics: false, ads: false, embeds: false, gpc: this.gpc, ts: 0 });
    // The native app never surfaces the web cookie banner; analytics is granted
    // later via grantAnalyticsConsent() once the user is authenticated.
    if (!this.isNative) {
      this.showBannerSubject.next(true);
    }
  }

  /** Parse the shared `tr_consent` cookie, or null if absent/malformed. */
  read(): ConsentState | null {
    const match = document.cookie.match(/(?:^|; )tr_consent=([^;]*)/);
    if (!match) {
      return null;
    }
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch {
      return null;
    }
  }

  /**
   * Ads consent to record with a new account: the visitor's effective choice from the
   * consent cookie; false when GPC or the native app rules ads out; null without a
   * cookie (no choice made), so the server applies the default for the visitor's region
   * (geoRegionForSignup).
   */
  adsConsentForSignup(): boolean | null {
    if (this.gpc || this.isNative) {
      return false;
    }
    const state = this.read();

    return state ? this.adsGranted(state.ads) : null;
  }

  /**
   * The visitor's current region (`CC`, or `US-<state>`), or null. A Cloudflare response
   * header rule on the app's hosts sets the host-only `tr_cf_geo` cookie on every page
   * load as `<country>/<ISO 3166-2 subdivision>`, e.g. `US/US-TX` or `DE/`. The API isn't
   * behind Cloudflare, so the server has no geo headers of its own.
   */
  geoRegionForSignup(): string | null {
    const match = document.cookie.match(/(?:^|; )tr_cf_geo=(?<country>[A-Z0-9]{2})\/(?<sub>[^;]*)/u);
    const country = match?.groups?.country;
    if (!country) {
      return null;
    }
    const state = country === 'US' ? /^US-(?<code>[A-Z]{2})$/u.exec(match.groups.sub)?.groups?.code : null;

    return state ? `US-${state}` : country;
  }

  /** Persist the choice to the shared cookie, applying GPC to ads. */
  write(choice: ConsentChoice): ConsentState {
    const data: ConsentState = {
      v: ConsentService.VERSION,
      analytics: !!choice.analytics,
      ads: this.adsGranted(!!choice.ads),
      embeds: !!choice.embeds,
      gpc: this.gpc,
      ts: Math.round(Date.now() / ConsentService.MS_PER_SEC),
    };

    const parts = [
      `${ConsentService.COOKIE}=${encodeURIComponent(JSON.stringify(data))}`,
      'Path=/',
      `Max-Age=${ConsentService.TTL_SECONDS}`,
      'SameSite=Lax',
    ];
    if (location.protocol === 'https:') {
      parts.push('Secure');
    }
    const domain = this.cookieDomain();
    if (domain) {
      parts.push(`Domain=${domain}`);
    }
    document.cookie = parts.join('; ');
    return data;
  }

  /**
   * Auto opt-in to analytics for authenticated app users (per the Terms). Keeps
   * any existing ads/embeds choice from a prior marketing decision intact and
   * never grants ads from a Terms click.
   */
  grantAnalyticsConsent(): void {
    const existing = this.read();
    if (existing?.analytics) {
      // Already opted in; just re-apply so GTM has the current signal this load.
      this.apply(existing);
      this.showBannerSubject.next(false);
      return;
    }
    this.decide({
      analytics: true,
      ads: !!existing?.ads,
      embeds: !!existing?.embeds,
    });
  }

  /** Banner "Accept all": opt in to analytics + ads (GPC still forces ads off). */
  acceptAll(): void {
    const existing = this.read();
    this.decide({ analytics: true, ads: true, embeds: !!existing?.embeds });
  }

  /** Banner "Reject all": clear every non-necessary category. */
  rejectAll(): void {
    this.decide({ analytics: false, ads: false, embeds: false });
  }

  /** Banner "Save preferences": persist the analytics + ads toggles, preserve embeds. */
  savePreferences(analytics: boolean, ads: boolean): void {
    const existing = this.read();
    this.decide({ analytics, ads, embeds: !!existing?.embeds });
  }

  /**
   * Hide the banner for this page session only, WITHOUT recording a choice.
   * No cookie is written, so the banner returns on the next load and we keep
   * asking until the visitor makes an explicit choice (Accept / Reject / Save).
   */
  dismissForSession(): void {
    this.showBannerSubject.next(false);
  }

  /** Write the choice, push it to GTM, and dismiss the banner. */
  private decide(choice: ConsentChoice): void {
    const saved = this.write(choice);
    this.apply(saved);
    this.showBannerSubject.next(false);
  }

  /**
   * Mirror consent to Google (Consent Mode) and the dataLayer signal — but only
   * when the effective state changed since the last emit this page load, so
   * consent-gated tags don't fire twice on a single load.
   */
  private apply(state: ConsentState): void {
    const adsEffective = this.adsGranted(state.ads);
    const key = `${Boolean(state.analytics)}|${Boolean(adsEffective)}|${Boolean(state.embeds)}`;
    if (key === this.lastEmittedKey) {
      return;
    }
    this.lastEmittedKey = key;
    this.updateGtag(state);
    this.pushSignal(state);
  }

  /** Google Consent Mode v2 update for Google tags (no-op if gtag absent). */
  private updateGtag(state: ConsentState): void {
    const gtag = (window as any).gtag;
    if (typeof gtag !== 'function') {
      return;
    }
    const ad = this.adsGranted(state.ads);
    gtag('consent', 'update', {
      /* eslint-disable @typescript-eslint/naming-convention */
      ad_storage: ad ? 'granted' : 'denied',
      ad_user_data: ad ? 'granted' : 'denied',
      ad_personalization: ad ? 'granted' : 'denied',
      analytics_storage: state.analytics ? 'granted' : 'denied',
      /* eslint-enable @typescript-eslint/naming-convention */
    });
  }

  /**
   * Push a per-category consent signal to the dataLayer so the GTM container can
   * gate non-Google tags by category. Matches the marketing site's contract.
   */
  private pushSignal(state: Pick<ConsentState, 'analytics' | 'ads' | 'embeds'>): void {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      /* eslint-disable @typescript-eslint/naming-convention */
      event: 'tr_consent_update',
      tr_necessary: true,
      tr_analytics: !!state.analytics,
      tr_ads: this.adsGranted(state.ads),
      tr_embeds: !!state.embeds,
      /* eslint-enable @typescript-eslint/naming-convention */
    });
  }

  /** `.tablesready.com` when on a tablesready host, else host-only (localhost/dev). */
  private cookieDomain(): string | null {
    const host = location.hostname;
    return host === 'tablesready.com' || /\.tablesready\.com$/.test(host)
      ? '.tablesready.com'
      : null;
  }
}
